import {Children, HTMLAttributes, cloneElement, forwardRef} from 'react';
import {cn} from '../../utils/cn';

type TableProps = {
  children: React.ReactNode;
} & HTMLAttributes<HTMLDivElement>;

export const Table = ({children, className, ...props}: TableProps) => {
  return (
    <div
      className={cn(
        'block relative rounded-[0.625rem] border border-neutral-300 border-solid color-black overflow-hidden',
        className,
      )}
      {...props}>
      <table className='w-full table-fixed'>{children}</table>
    </div>
  );
};

export const Tr = forwardRef<
  HTMLTableRowElement,
  HTMLAttributes<HTMLTableRowElement>
>(({className, ...props}, ref) => {
  return (
    <tr
      ref={ref}
      className={cn(
        'relative flex cursor-pointer hover:bg-[#f3f3f3] max-w-full',
        className,
      )}
      {...props}>
      {props.children}
    </tr>
  );
});

Tr.displayName = 'Tr';

export const Th = forwardRef<
  HTMLTableCellElement,
  HTMLAttributes<HTMLTableCellElement>
>(({className, ...props}, ref) => {
  return (
    <th
      ref={ref}
      className={cn(
        'basis-full px-[1.625rem] py-4 text-left font-medium',
        className,
      )}
      {...props}>
      {props.children}
    </th>
  );
});

Th.displayName = 'Th';

interface TdProps extends HTMLAttributes<HTMLTableCellElement> {
  colSpan?: number;
}

export const Td = forwardRef<HTMLTableCellElement, TdProps>(
  ({className, colSpan, ...props}, ref) => {
    return (
      <td
        ref={ref}
        className={cn('basis-full px-[1.625rem] py-4', className)}
        colSpan={colSpan}
        {...props}>
        {props.children}
      </td>
    );
  },
);

Td.displayName = 'Td';

export const Thead = ({
  className,
  ...props
}: HTMLAttributes<HTMLTableSectionElement>) => {
  const childrenArray = Array.isArray(props.children)
    ? props.children
    : [props.children];

  return (
    <thead
      className={cn(
        'block bg-purple-100 font-medium border-b-neutral-300 border-b border-solid',
        className,
      )}
      {...props}>
      {Children.map(childrenArray, child => {
        return cloneElement(child, {
          className: cn(
            child.props.className,
            'cursor-default hover:bg-transparent',
          ),
        });
      })}
    </thead>
  );
};

export const Tbody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({className, ...props}, ref) => {
  const childrenArray = Array.isArray(props.children)
    ? props.children
    : [props.children];

  return (
    <tbody ref={ref} className={cn('block', className)} {...props}>
      {Children.map(childrenArray, child => {
        if (!child) {
          return null;
        }

        const isLastChild =
          childrenArray.indexOf(child) === childrenArray.length - 1;

        if (isLastChild) {
          return cloneElement(child);
        }

        return cloneElement(child, {
          className: cn(
            'flex border-b border-b-solid border-neutral-300',
            child.props?.className,
          ),
        });
      })}
    </tbody>
  );
});

Tbody.displayName = 'Tbody';

export const Tfoot = (props: HTMLAttributes<HTMLTableSectionElement>) => {
  return <tfoot {...props}>{props.children}</tfoot>;
};
